// Contexto de Organización
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiService from '@/services/api';
import { API_ENDPOINTS } from '@/config/api';
import type { Organization, ApiResponse } from '@/types';

interface OrganizationContextType {
  organization: Organization | null;
  isLoading: boolean;
  refreshOrganization: () => Promise<void>;
  updateOrganization: (data: Partial<Organization>) => Promise<void>;
  updateConfig: (config: Record<string, any>) => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Cargar organización
   */
  const loadOrganization = async () => {
    // Solo cargar si hay datos de sesión en localStorage (la cookie httpOnly
    // la valida el backend, pero evitamos requests innecesarios en /login)
    if (!localStorage.getItem('user')) return;

    try {
      setIsLoading(true);
      const response = await apiService.get<ApiResponse<Organization>>(
        API_ENDPOINTS.ORGANIZATION
      );

      if (response.success && response.data) {
        setOrganization(response.data);
      }
    } catch (error: any) {
      setOrganization(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refrescar organización
   */
  const refreshOrganization = async () => {
    await loadOrganization();
  };

  /**
   * Actualizar organización
   */
  const updateOrganization = async (data: Partial<Organization>) => {
    try {
      const response = await apiService.put<ApiResponse<Organization>>(
        API_ENDPOINTS.ORGANIZATION,
        data
      );

      if (response.success && response.data) {
        setOrganization(response.data);
      }
    } catch (error) {
      console.error('Error al actualizar organización:', error);
      throw error;
    }
  };

  // Cargar organización al montar (la cookie access_token determina si hay sesión)
  useEffect(() => {
    loadOrganization();
  }, []);

  // Escuchar eventos de autenticación
  useEffect(() => {
    // Escuchar evento de login desde AuthContext
    const handleAuthLogin = () => {
      console.log('[OrganizationContext] Evento auth-login recibido');
      loadOrganization();
    };

    // Escuchar evento de logout
    const handleAuthLogout = () => {
      console.log('[OrganizationContext] Evento auth-logout recibido');
      setOrganization(null);
      setIsLoading(false);
    };

    window.addEventListener('auth-login', handleAuthLogin);
    window.addEventListener('auth-logout', handleAuthLogout);

    return () => {
      window.removeEventListener('auth-login', handleAuthLogin);
      window.removeEventListener('auth-logout', handleAuthLogout);
    };
  }, []); // Sin dependencias, los handlers usan las funciones más recientes

  /**
   * Actualizar (merge) la config JSONB de la organización.
   * Ej: updateConfig({ ui_settings: { hide_unavailable_modules: true } })
   * El backend mergea a nivel de PUT /organizations/config — ver Organization.updateConfig.
   */
  const updateConfig = async (config: Record<string, any>) => {
    try {
      const response = await apiService.put<ApiResponse<Organization>>(
        API_ENDPOINTS.ORGANIZATION_CONFIG,
        { config }
      );

      if (response.success && response.data) {
        setOrganization(response.data);
      }
    } catch (error) {
      console.error('Error al actualizar configuración de la organización:', error);
      throw error;
    }
  };

  const value: OrganizationContextType = {
    organization,
    isLoading,
    refreshOrganization,
    updateOrganization,
    updateConfig,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

/**
 * Hook para usar el contexto de organización
 */
export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization debe ser usado dentro de un OrganizationProvider');
  }
  return context;
};

export default OrganizationContext;
